import React, { useEffect, useState } from 'react';
import OtpInput from 'react-otp-input';
import etherLogo from '../../../assets/images/ether_logo.png';
import { OtpSentProps } from '../../HomePage/types';
import AdminAuthContainer from '../AdminAuthContainer';
import {
  HintText,
  StyledEmail,
  StyledHeader,
  SubHeader,
  SubText
} from '../styled';
import { getFooterLinks } from '../../HomePage/constants/login';
import OtpErrorMessage from '../OtpErrorMessage';
import { useNavigate } from 'react-router-dom';
import '../css/otpInput.css';
import { Link, Typography } from '@mui/material';
import loginAPi from '../../../apis/login';
import ToastMessage from '../../../components/ToastMessage';
import { SnackbarSeverityType } from '../../../enums';

const AdminOtpScreen = ({
  otp,
  onOtpChange,
  onSubmit,
  errorMessage,
  setErrorMessage,
  email,
  isLoading
}: OtpSentProps) => {
  const navigate = useNavigate();
  const isOtpValid = otp.length === 4;
  const [isLinkDisabled, setIsLinkDisabled] = useState<boolean>(true);
  const [countdown, setCountdown] = useState<number>(60);
  const [focusResetKey, setFocusResetKey] = useState<number>(0);
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [successMessage, setSuccessMessage] = useState<string>('');
  const isAdminLogin = true;

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = () => setToastOpen(false);

  const handleResendOtp = async () => {
    setSuccessMessage('');
    setErrorMessage('');
    onOtpChange('');
    setFocusResetKey((key) => key + 1);
    setIsLinkDisabled(true);
    setCountdown(60);
    try {
      await loginAPi.doLogin(email, isAdminLogin);
      setSuccessMessage('A new OTP has been sent to your Email ID');
    } catch (error: any) {
      const msg = 'OTP resend failed. Please try again.';
      setErrorMessage(msg);
      showToast(msg, SnackbarSeverityType.error);
    }
  };

  useEffect(() => {
    if (!isLinkDisabled) return;
    if (countdown <= 0) {
      setIsLinkDisabled(false);
      setSuccessMessage('');
      return;
    }
    const timerId = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => {
      clearTimeout(timerId);
    };
  }, [isLinkDisabled, countdown]);

  useEffect(() => {
    if (errorMessage) {
      showToast(errorMessage, SnackbarSeverityType.error);
    }
    // eslint-disable-next-line
  }, [errorMessage]);

  return (
    <AdminAuthContainer
      logoSrc={String(etherLogo)}
      sections={[
        { title: 'OTP Sent!', titleStyle: StyledHeader },
        {
          subtext: (
            <>
              A one-time password (OTP) has been sent to{' '}
              <StyledEmail>{email}</StyledEmail>. Please check your inbox and
              enter the OTP below to log in to Ether.
            </>
          ),
          subtextStyle: SubText
        },
        { title: 'Verify With OTP', titleStyle: SubHeader },
        {
          subtext: <HintText>Enter 4 digit OTP here</HintText>,
          subtextStyle: SubText
        }
      ]}
      buttonText='Login to Ether'
      onSubmit={onSubmit}
      footerText='By proceeding, I agree to the '
      footerLinks={getFooterLinks(navigate)}
      isLoading={isLoading}
      isButtonDisabled={!isOtpValid}
    >
      <OtpInput
        key={`otp-${focusResetKey}`}
        value={otp}
        onChange={onOtpChange}
        numInputs={4}
        renderInput={(props) => (
          <input
            {...props}
            className={`otp-input ${errorMessage ? 'error' : ''}`}
          />
        )}
        inputType='tel'
        shouldAutoFocus
        inputStyle='otp-input'
      />
      <Typography variant='caption' color='textSecondary' sx={{ mt: 2 }}>
        {isLinkDisabled ? (
          `Click here to resend OTP in ${countdown} sec${countdown === 1 ? '' : 's'}`
        ) : (
          <Link
            component='span'
            onClick={handleResendOtp}
            underline='none'
            sx={{ cursor: 'pointer', textDecoration: 'none', pt: 2 }}
          >
            Click here to resend OTP
          </Link>
        )}
      </Typography>
      <br />
      {successMessage && (
        <Typography variant='caption' color='primary' sx={{ mt: 1 }}>
          {successMessage}
        </Typography>
      )}
      {errorMessage && <OtpErrorMessage errorMessage={errorMessage} />}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </AdminAuthContainer>
  );
};

export default AdminOtpScreen;
